/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { Redirect, Switch } from "react-router-dom";
import { Layout } from "../../_metronic/layout";
import { Logout, AuthPage } from "./../modules/Auth";
import ErrorsPage from "./../modules/ErrorsExamples/ErrorsPage";
import BillsListPage from "./../modules/Bills/pages/BillsListPage";
import OnBoarding from "./../modules/Auth/pages/OnBoarding";
import Login from "./../modules/Auth/pages/Login";
import TeamsListPage from "./../modules/Teams/pages/TeamsListPage";
import PrivateRoute from "./PrivateRoute";
import PublicRoute from "./PublicRoute";
import BillPage from "./../modules/Bills/pages/BillPage";
import TeamPage from "./../modules/Teams/pages/TeamPage";
import Registration from "./../modules/Auth/pages/Registration";
import ForgotPassword from "./../modules/Auth/pages/ForgotPassword";
import { BuilderPage } from "./../pages/BuilderPage";
import { DashboardPage } from "./../pages/DashboardPage";
import DocumentPage from "./../modules/Bills/pages/DocumentPage";
import BillPageDesign from "./../modules/Bills/pages/BillPageDesign";
import ProposalsReport from "./../modules/Bills/pages/ProposalsReport";
import BillReport from "./../modules/Bills/pages/BillReport";
import TeamsReport from "./../modules/Bills/pages/TeamsReport";
import ReactBootstrapPage from "./../modules/ReactBootstrapExamples/ReactBootstrapPage";
import MyProfilePage from "../modules/MyProfile/MyProfilePage";
import MyActivity from "../modules/MyActivity/MyActivity";
import QuestionAndAnswer from "../modules/QuestionAndAnswer/QuestionAndAnswer";
import Privacy from "../modules/Auth/pages/Privacy/Privacy";
import Terms from "../modules/Auth/pages/Terms/Terms";
import { useFlags, FeatureMap } from "../../config/featureFlags";
import ResetPassword from "../modules/Auth/pages/ResetPassword";
import Invitations from "../modules/Invitations/Pages/Invitations";
import FbDelete from "../modules/Auth/pages/FbDelete/FbDelete";
import ContactPage from "../pages/ContactPage";

// Change route based on SITE TYPE backend functionality is the same
const routesConfig = (features: FeatureMap) => {
  const routerName = features.DISPLAY_LIKUMI_CONTENT
    ? "bills"
    : "policyProposals";

  return [
    {
      exact: true,
      path: "/",
      component: BillsListPage,
      Layout: Layout,
    },
    {
      path: "/dashboard",
      component: DashboardPage,
      Layout: Layout,
      private: true,
    },
    {
      path: "/builder",
      component: BuilderPage,
      Layout: Layout,
      private: true,
    },
    {
      path: `/${routerName}/:id/documents/:documentId`,
      component: DocumentPage,
      Layout: Layout,
      private: true,
    },
    {
      path: `/${routerName}/:id`,
      component: BillPage,
      Layout: Layout,
    },
    {
      path: `/${routerName}`,
      component: BillsListPage,
      Layout: Layout,
    },
    {
      path: "/bill-design",
      component: BillPageDesign,
      Layout: Layout,
      private: true,
    },
    {
      path: "/team-report/:id",
      component: TeamsReport,
      Layout: Layout,
      private: true,
    },
    {
      path: "/proposal-report/:id",
      component: ProposalsReport,
      Layout: Layout,
      private: true,
    },
    {
      path: "/bill-report/:id",
      component: BillReport,
      Layout: Layout,
      private: true,
    },
    {
      path: "/teams/:id",
      component: TeamPage,
      Layout: Layout,
      private: true,
    },
    {
      path: "/teams",
      component: TeamsListPage,
      Layout: Layout,
      private: true,
    },
    {
      path: "/react-bootstrap",
      component: ReactBootstrapPage,
      Layout: Layout,
      private: true,
    },
    {
      path: "/auth/login",
      component: Login,
      Layout: AuthPage,
    },
    {
      path: "/auth/registration",
      component: Registration,
      Layout: AuthPage,
    },
    {
      path: "/myProfile",
      component: MyProfilePage,
      Layout: Layout,
    },
    {
      path: "/question-and-answer",
      component: QuestionAndAnswer,
      Layout: Layout,
    },
    {
      path: "/onboarding",
      component: OnBoarding,
      Layout: AuthPage,
      private: true,
    },
    {
      path: "/auth/forgot-password",
      component: ForgotPassword,
      Layout: AuthPage,
    },
    {
      path: "/auth/reset-password",
      component: ResetPassword,
      Layout: AuthPage,
    },
    {
      path: "/logout",
      component: Logout,
      private: true,
    },
    {
      path: "/privacy",
      component: Privacy,
      Layout: Layout,
    },
    {
      path: "/terms",
      component: Terms,
      Layout: Layout,
    },
    {
      path: "/instruction-delete-fb",
      component: FbDelete,
      Layout: Layout,
    },
    {
      path: "/Invitations",
      component: Invitations,
    },
    {
      path: "/error",
      component: ErrorsPage,
      Layout: Layout,
    },
    {
      path: "/contacts",
      component: ContactPage,
      Layout: Layout,
    }
  ];
};

export function Routes() {
  const features = useFlags();
  const routes = routesConfig(features);

  return (
    <Switch>
      {routes.map((route, item) =>
        route.private ? (
          <PrivateRoute key={item} {...route} />
        ) : (
          <PublicRoute key={item} {...route} />
        )
      )}
      <Redirect to="error/error-v1" />
    </Switch>
  );
}
